/* eslint-disable max-lines */
import type { ReactElement } from "react";
import { SubtitleManager, type ProviderSubtitle } from "../../../../shared/components/subtitle";
import { ThumbnailScrubber } from "../../../admin/bulk-edit/components/ThumbnailScrubber";
import type { SubtitleMetadata } from "../../hooks/useContentEditor";
import type { TitleData } from "../../hooks/useTitleMutations";
import { VideoUploadZone } from "./VideoUploadZone";

interface UploadStepProps {
  submissionId: string | null;
  formData: {
    featureAssetId?: string;
    featureVideoUrl?: string;
    trailerAssetId?: string;
    trailerVideoUrl?: string;
    featureSubtitles?: SubtitleMetadata[];
    trailerSubtitles?: SubtitleMetadata[];
  };
  onChange: (field: string, assetId: string, videoUrl: string) => void;
  onSubtitleChange?: (assetKind: "feature" | "trailer", subtitles: SubtitleMetadata[]) => void;
  onSaveDraft?: () => void;
  savingDraft?: boolean;
  mode?: "submission" | "title"; // Which entity type we're editing
  titleData?: TitleData | null; // Raw title data for admin mode (includes assets with subtitles)
  onRefreshTitle?: () => Promise<void>; // Callback to refresh title data after subtitle changes
}

/**
 * Subtitle management section - always visible (no collapsing)
 * Shows existing subtitles and upload zone in a clean card layout
 *
 * Modes:
 * - "title" (admin mode): Uses SubtitleManager with mode="admin" for Cloudflare Stream
 * - "submission" (provider mode): Uses SubtitleManager with mode="provider" for R2 staging
 */
function SubtitleSection({
  assetKind,
  submissionId,
  providerSubtitles,
  onSubtitleChange,
  mode,
  assetId,
  existingSubtitles,
  onRefreshTitle,
}: Readonly<{
  assetKind: "feature" | "trailer";
  submissionId: string | null;
  providerSubtitles?: SubtitleMetadata[];
  onSubtitleChange?: (assetKind: "feature" | "trailer", subtitles: SubtitleMetadata[]) => void;
  mode?: "submission" | "title";
  assetId?: string;
  existingSubtitles?: Array<{
    id: string;
    lang: string;
    format: string;
    url: string;
    forced: boolean;
    metadata?: Record<string, unknown> | null;
  }>;
  onRefreshTitle?: () => Promise<void>;
}>): ReactElement {
  const isAdminMode = mode === "title";

  // Admin mode: use SubtitleManager with mode="admin" for Cloudflare Stream
  if (isAdminMode && assetId && onRefreshTitle) {
    return (
      <div className="pt-8 border-t border-neutral-800">
        <SubtitleManager
          mode="admin"
          assetId={assetId}
          assetKind={assetKind}
          existingSubtitles={existingSubtitles ?? []}
          onRefresh={onRefreshTitle}
        />
      </div>
    );
  }

  // Provider mode: use SubtitleManager with mode="provider" for R2 staging
  // Requires a callback handler for subtitle changes since we manage state locally
  const handleProviderSubtitlesChange = (subtitles: ProviderSubtitle[]): void => {
    if (onSubtitleChange) {
      // Convert ProviderSubtitle to SubtitleMetadata (they're compatible)
      const converted: SubtitleMetadata[] = subtitles.map((sub) => ({
        language: sub.language,
        label: sub.label,
        fileName: sub.fileName,
        r2Key: sub.r2Key,
        format: sub.format,
        processing: sub.processing ?? {
          cueCount: 0,
          sizeKB: 0,
          wasConverted: false,
          wasNormalized: false,
          timecodeOffset: 0,
        },
        validation: sub.validation,
      }));
      onSubtitleChange(assetKind, converted);
    }
  };

  return (
    <div className="pt-8 border-t border-neutral-800 space-y-2">
      {onSubtitleChange && submissionId && (
        <SubtitleManager
          mode="provider"
          submissionId={submissionId}
          assetKind={assetKind}
          providerSubtitles={providerSubtitles ?? []}
          onRefresh={async () => {
            // Provider mode doesn't need to refresh from server
            // Changes are managed locally via onProviderSubtitlesChange
          }}
          onProviderSubtitlesChange={handleProviderSubtitlesChange}
        />
      )}
    </div>
  );
}

/**
 * Thumbnail preview section for admin mode
 * Shows scrubber when VTT thumbnails are available
 */
function ThumbnailSection({
  thumbnailVttUrl,
  durationSec,
}: Readonly<{
  thumbnailVttUrl: string | null | undefined;
  durationSec: number | null | undefined;
}>): ReactElement | null {
  if (!thumbnailVttUrl || !durationSec) {
    return null;
  }

  return (
    <div className="mt-2 py-2 space-y-3">
      <div className="">
        <ThumbnailScrubber vttUrl={thumbnailVttUrl} duration={durationSec} />
      </div>
    </div>
  );
}

/**
 * Video section with upload zone, thumbnails, and subtitles
 */
function VideoSection({
  label,
  assetKind,
  submissionId,
  videoUrl,
  maxSizeGB,
  onChange,
  onSubtitleChange,
  mode,
  assetId,
  existingSubtitles,
  providerSubtitles,
  thumbnailVttUrl,
  durationSec,
  onRefreshTitle,
}: Readonly<{
  label: string;
  assetKind: "FEATURE" | "TRAILER";
  submissionId: string | null;
  videoUrl?: string;
  maxSizeGB: number;
  onChange: (assetId: string, videoUrl: string) => void;
  onSubtitleChange?: (assetKind: "feature" | "trailer", subtitles: SubtitleMetadata[]) => void;
  mode?: "submission" | "title";
  assetId?: string;
  existingSubtitles?: Array<{
    id: string;
    lang: string;
    format: string;
    url: string;
    forced: boolean;
  }>;
  providerSubtitles?: SubtitleMetadata[];
  thumbnailVttUrl: string | null | undefined;
  durationSec: number | null | undefined;
  onRefreshTitle?: () => Promise<void>;
}>): ReactElement {
  const subtitleAssetKind = assetKind.toLowerCase() as "feature" | "trailer";
  const isAdminMode = mode === "title";

  return (
    <div className="space-y-4">
      {/* Video upload zone */}
      <VideoUploadZone
        label={label}
        assetKind={assetKind}
        submissionId={mode === "title" ? null : submissionId}
        titleId={mode === "title" ? submissionId : null}
        maxSizeGB={maxSizeGB}
        onChange={onChange}
        value={videoUrl || null}
        mode={mode === "title" ? "title" : "submission"}
        assetId={assetId}
      />

      {/* Thumbnail preview (admin mode only, when video exists) */}
      {videoUrl && isAdminMode && <ThumbnailSection thumbnailVttUrl={thumbnailVttUrl} durationSec={durationSec} />}

      {/* Subtitle section (when video exists) */}
      {videoUrl && (
        <SubtitleSection
          assetKind={subtitleAssetKind}
          submissionId={submissionId}
          providerSubtitles={providerSubtitles}
          onSubtitleChange={onSubtitleChange}
          mode={mode}
          assetId={assetId}
          existingSubtitles={existingSubtitles}
          onRefreshTitle={onRefreshTitle}
        />
      )}
    </div>
  );
}

/**
 * Upload Step - Feature Film and Trailer video uploads + subtitles + thumbnails
 */
export function UploadStep({
  submissionId,
  formData,
  onChange,
  onSubtitleChange,
  onSaveDraft,
  savingDraft,
  mode = "submission",
  titleData,
  onRefreshTitle,
}: Readonly<UploadStepProps>): ReactElement {
  // Extract asset data for admin mode
  const featureAsset = titleData?.assets?.find((a) => a.kind === "FEATURE");
  const trailerAsset = titleData?.assets?.find((a) => a.kind === "TRAILER");

  return (
    <div className="space-y-6 pb-2">
      <div>
        <h2 className="text-2xl font-bold text-white mb-2">Video Upload</h2>
        <p className="text-sm text-neutral-400 pb-6">Upload your feature film and trailer.</p>
      </div>

      {!submissionId && (
        <div className="bg-yellow-900/20 border border-yellow-700 rounded-lg p-4">
          <div className="flex items-start justify-between gap-4">
            <p className="text-sm text-yellow-400 flex-1">
              Please save your submission as a draft before uploading videos.
            </p>
            {onSaveDraft && (
              <button
                type="button"
                onClick={onSaveDraft}
                disabled={savingDraft}
                className="px-4 py-2 bg-lumi-accent text-black text-sm font-semibold rounded-lg hover:bg-lumi-accent/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {savingDraft ? "Saving..." : "Save Draft"}
              </button>
            )}
          </div>
        </div>
      )}

      <div className="space-y-8 md:grid md:grid-cols-2 md:gap-8 md:space-y-0">
        <VideoSection
          label="Feature Film"
          assetKind="FEATURE"
          submissionId={submissionId}
          videoUrl={formData.featureVideoUrl}
          maxSizeGB={50}
          onChange={(assetId, videoUrl) => onChange("feature", assetId, videoUrl)}
          onSubtitleChange={onSubtitleChange}
          mode={mode}
          assetId={featureAsset?.id}
          existingSubtitles={featureAsset?.subtitles}
          providerSubtitles={formData.featureSubtitles}
          thumbnailVttUrl={featureAsset?.thumbnailVttUrl ?? null}
          durationSec={featureAsset?.durationSec ?? null}
          onRefreshTitle={onRefreshTitle}
        />

        <VideoSection
          label="Trailer"
          assetKind="TRAILER"
          submissionId={submissionId}
          videoUrl={formData.trailerVideoUrl}
          maxSizeGB={5}
          onChange={(assetId, videoUrl) => onChange("trailer", assetId, videoUrl)}
          onSubtitleChange={onSubtitleChange}
          mode={mode}
          assetId={trailerAsset?.id}
          existingSubtitles={trailerAsset?.subtitles}
          providerSubtitles={formData.trailerSubtitles}
          thumbnailVttUrl={trailerAsset?.thumbnailVttUrl ?? null}
          durationSec={trailerAsset?.durationSec ?? null}
          onRefreshTitle={onRefreshTitle}
        />
      </div>
    </div>
  );
}
